(() => {
  if (customElements.get('universal-product-options')) return;

  class UniversalProductOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.ui = this.querySelector('[data-product-options-ui]');
      this.error = this.querySelector('[data-product-options-error]');
      this.selection = new Map();
      this.form = null;

      try {
        const source = this.querySelector('[data-product-options-schema]');
        this.schema = JSON.parse(source?.textContent || '{}');
        const imageSource = this.querySelector('[data-product-option-images]');
        const optionImages = JSON.parse(imageSource?.textContent || '{}');
        this.optionImages = optionImages && typeof optionImages === 'object' && !Array.isArray(optionImages)
          ? optionImages
          : {};
        const variantSource = this.querySelector('[data-product-variant-prices]');
        const variants = JSON.parse(variantSource?.textContent || '[]');
        this.variantPrices = new Map(variants.map((variant) => [String(variant.id), Number(variant.price)]));
      } catch (error) {
        this.fail('Product options could not be loaded. Please contact us for assistance.');
        return;
      }

      if (!Array.isArray(this.schema.groups)) return;
      this.createTooltip();
      this.groupsById = new Map(this.schema.groups.map((group) => [group.id, group]));
      this.valuesById = new Map();
      this.childrenByValue = new Map();
      this.rootGroups = [];
      for (const group of this.schema.groups) {
        for (const value of group.values || []) this.valuesById.set(value.id, value);
        const activation = group.show_when?.value_id;
        if (!activation) this.rootGroups.push(group);
        else {
          const children = this.childrenByValue.get(activation) || [];
          children.push(group);
          this.childrenByValue.set(activation, children);
        }
      }
      this.render();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundPositionTooltip);
      window.removeEventListener('scroll', this.boundPositionTooltip, true);
      this.tooltip?.remove();
    }

    selectedIds(exceptGroupId = '') {
      return new Set(
        [...this.selection.entries()]
          .filter(([groupId, valueId]) => groupId !== exceptGroupId && valueId)
          .map(([, valueId]) => valueId)
      );
    }

    isGroupActive(group) {
      const activation = group.show_when?.value_id;
      return !activation || this.selectedIds().has(activation);
    }

    flowGroups() {
      const groups = [];
      const visited = new Set();
      const visit = (group) => {
        if (!group || visited.has(group.id)) return;
        visited.add(group.id);
        groups.push(group);
        const selectedValue = this.selection.get(group.id);
        if (!selectedValue) return;
        for (const child of this.childrenByValue.get(selectedValue) || []) visit(child);
      };
      for (const root of this.rootGroups) visit(root);
      return groups;
    }

    activeGroups() {
      return this.flowGroups();
    }

    isGroupComplete(group) {
      if (!this.selection.has(group.id)) return false;
      return !group.required || Boolean(this.selection.get(group.id));
    }

    flowStates() {
      let waiting = false;
      return this.flowGroups().map((group) => {
        if (waiting) return { group, state: 'locked' };
        if (this.isGroupComplete(group)) return { group, state: 'complete' };
        waiting = true;
        return { group, state: 'current' };
      });
    }

    clearInactiveSelections() {
      let changed = true;
      while (changed) {
        changed = false;
        for (const [groupId] of this.selection) {
          const group = this.groupsById.get(groupId);
          if (group && !this.isGroupActive(group)) {
            this.selection.delete(groupId);
            changed = true;
          }
        }
      }
    }

    findValue(group, valueId) {
      return (group.values || []).find((value) => value.id === valueId);
    }

    isValueDisabled(group, value) {
      const selected = this.selectedIds(group.id);
      return (value.disabled_with || []).some((valueId) => selected.has(valueId));
    }

    disabledReason(value, concise = false) {
      const selected = this.selectedIds();
      const conflicts = (value.disabled_with || []).filter((valueId) => selected.has(valueId));
      if (!conflicts.length) return '';
      const labels = conflicts.map((valueId) => this.valuesById.get(valueId)?.label || valueId);
      const message = `Unavailable with ${labels.join(', ')}`;
      return concise ? message : `${message}. Change an earlier selection to use this option.`;
    }

    choose(groupId, valueId) {
      this.hideTooltip();
      const flow = this.flowGroups();
      const groupIndex = flow.findIndex((group) => group.id === groupId);
      for (const downstream of flow.slice(groupIndex + 1)) this.selection.delete(downstream.id);
      const group = this.groupsById.get(groupId);
      if (valueId || (group && (!group.required || group.allow_none))) this.selection.set(groupId, valueId);
      else this.selection.delete(groupId);
      this.clearInactiveSelections();
      this.hideError();
      this.advanceFocus = true;
      this.render();
    }

    editGroup(groupId) {
      const flow = this.flowGroups();
      const groupIndex = flow.findIndex((group) => group.id === groupId);
      for (const group of flow.slice(Math.max(0, groupIndex))) this.selection.delete(group.id);
      this.clearInactiveSelections();
      this.advanceFocus = true;
      this.render();
    }

    render() {
      this.hideTooltip();
      this.ui.replaceChildren();
      const states = this.flowStates();
      for (const { group, state } of states) {
        this.ui.append(this.renderGroup(group, state));
      }
      this.ui.append(this.renderSummary(states));
      this.syncProductForm();
      if (this.advanceFocus) {
        this.advanceFocus = false;
        requestAnimationFrame(() => {
          const current = this.querySelector('.universal-product-options__group--current');
          current?.querySelector('.universal-product-options__legend')?.focus({ preventScroll: true });
          current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }

    renderGroup(group, state) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = `universal-product-options__group universal-product-options__group--${state}`;
      if (group.show_when?.value_id) fieldset.classList.add('universal-product-options__group--nested');
      fieldset.dataset.optionGroup = group.id;
      fieldset.dataset.optionState = state;

      const legend = document.createElement('legend');
      legend.className = 'universal-product-options__legend';
      if (state === 'current') legend.tabIndex = -1;
      legend.textContent = group.label;
      if (group.required) {
        const required = document.createElement('span');
        required.className = 'universal-product-options__required';
        required.textContent = '*';
        required.setAttribute('aria-label', 'required');
        legend.append(required);
      }
      fieldset.append(legend);

      if (state === 'locked') {
        const locked = document.createElement('span');
        locked.className = 'universal-product-options__locked';
        locked.textContent = 'Complete the previous step to unlock';
        fieldset.append(locked);
        return fieldset;
      }
      if (state === 'complete') {
        fieldset.append(this.renderCompletedGroup(group));
        return fieldset;
      }

      const prompt = document.createElement('span');
      prompt.className = 'universal-product-options__prompt';
      prompt.textContent = 'Choose one option to continue';
      fieldset.append(prompt);
      if (group.presentation === 'select') fieldset.append(this.renderSelect(group));
      else fieldset.append(this.renderCards(group));
      return fieldset;
    }

    renderCompletedGroup(group) {
      const selectedId = this.selection.get(group.id);
      const value = this.findValue(group, selectedId);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'universal-product-options__completed';
      button.setAttribute('aria-label', `Change ${group.label}`);

      const imageUrl = value ? this.thumbnailUrl(value) : '';
      if (imageUrl) {
        const thumbnail = document.createElement('img');
        thumbnail.className = 'universal-product-options__completed-thumbnail';
        thumbnail.src = imageUrl;
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        button.append(thumbnail);
      }
      const label = document.createElement('span');
      label.className = 'universal-product-options__completed-label';
      label.textContent = value?.label || 'No option';
      const change = document.createElement('span');
      change.className = 'universal-product-options__change';
      change.textContent = 'Change';
      button.append(label, change);
      button.addEventListener('click', () => this.editGroup(group.id));
      return button;
    }

    renderSelect(group) {
      const select = document.createElement('select');
      select.className = 'universal-product-options__select';
      select.name = `product-option-${group.id}`;
      select.setAttribute('aria-label', group.label);
      if (group.required) select.required = true;

      if (!this.selection.has(group.id)) {
        const prompt = new Option('Choose an option', '', true, true);
        prompt.disabled = true;
        select.append(prompt);
      }
      if (group.allow_none || !group.required) {
        select.append(new Option('No option', '', false, this.selection.has(group.id) && this.selection.get(group.id) === ''));
      }

      for (const value of group.values || []) {
        const unavailableReason = this.disabledReason(value, true);
        const label = `${value.label}${this.deltaLabel(value.price_delta_cents)}${unavailableReason ? ` — ${unavailableReason}` : ''}`;
        const option = new Option(label, value.id, false, this.selection.get(group.id) === value.id);
        option.disabled = Boolean(unavailableReason);
        select.append(option);
      }
      select.addEventListener('change', () => this.choose(group.id, select.value));
      return select;
    }

    renderCards(group) {
      const choices = document.createElement('div');
      choices.className = 'universal-product-options__choices';
      const container = document.createElement('div');
      container.className = 'universal-product-options__cards';
      const values = [...(group.values || [])];
      if (group.allow_none || !group.required) {
        values.unshift({ id: '', label: 'No option', price_delta_cents: 0 });
      }

      for (const value of values) {
        const card = document.createElement('label');
        card.className = 'universal-product-options__card';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `product-option-${group.id}`;
        input.value = value.id;
        input.checked = this.selection.has(group.id) && this.selection.get(group.id) === value.id;
        input.disabled = value.id ? this.isValueDisabled(group, value) : false;
        if (value.description) input.setAttribute('aria-describedby', this.tooltip.id);
        input.addEventListener('change', () => this.choose(group.id, value.id));
        card.append(input);

        const imageUrl = this.thumbnailUrl(value);
        if (imageUrl) {
          card.classList.add('universal-product-options__card--has-image');
          const thumbnail = document.createElement('span');
          thumbnail.className = 'universal-product-options__thumbnail';
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.addEventListener('error', () => {
            thumbnail.remove();
            card.classList.remove('universal-product-options__card--has-image');
          });
          thumbnail.append(image);
          card.append(thumbnail);
        }

        const content = document.createElement('span');
        content.className = 'universal-product-options__card-content';
        const label = document.createElement('span');
        label.className = 'universal-product-options__label';
        label.textContent = value.label;
        content.append(label);
        if (value.price_delta_cents) {
          const delta = document.createElement('span');
          delta.className = 'universal-product-options__delta';
          delta.textContent = this.deltaLabel(value.price_delta_cents).trim();
          content.append(delta);
        }
        card.append(content);

        const unavailableReason = value.id ? this.disabledReason(value) : '';
        if (unavailableReason) {
          card.classList.add('universal-product-options__card--unavailable');
          card.setAttribute('aria-label', `${value.label}. ${unavailableReason}`);
          const unavailable = document.createElement('span');
          unavailable.className = 'universal-product-options__unavailable';
          unavailable.textContent = 'Unavailable';
          content.append(unavailable);
        }
        const tooltipValue = unavailableReason ? { ...value, description: unavailableReason } : value;
        if (tooltipValue.description) {
          card.addEventListener('mouseenter', () => this.showTooltip(card, tooltipValue));
          card.addEventListener('mouseleave', () => this.hideTooltip());
          card.addEventListener('focusin', () => this.showTooltip(card, tooltipValue));
          card.addEventListener('focusout', () => this.hideTooltip());
        }
        container.append(card);
      }
      choices.append(container);
      return choices;
    }

    thumbnailUrl(value) {
      const candidate = value.image_url || value.thumbnail_url || this.optionImages?.[value.id];
      return typeof candidate === 'string' ? candidate.trim() : '';
    }

    createTooltip() {
      UniversalProductOptions.tooltipCount = (UniversalProductOptions.tooltipCount || 0) + 1;
      this.tooltip = document.createElement('div');
      this.tooltip.id = `universal-product-options-tooltip-${UniversalProductOptions.tooltipCount}`;
      this.tooltip.className = 'universal-product-options__tooltip';
      this.tooltip.role = 'tooltip';
      this.tooltip.hidden = true;
      document.body.append(this.tooltip);
      this.boundPositionTooltip = () => this.positionTooltip();
      window.addEventListener('resize', this.boundPositionTooltip);
      window.addEventListener('scroll', this.boundPositionTooltip, true);
    }

    showTooltip(anchor, value) {
      if (!this.tooltip || !value?.description) return;
      const title = document.createElement('span');
      title.className = 'universal-product-options__tooltip-title';
      title.textContent = value.label;
      const description = document.createElement('span');
      description.className = 'universal-product-options__tooltip-description';
      description.textContent = value.description;
      this.tooltip.replaceChildren(title, description);

      const styles = getComputedStyle(this);
      const background = styles.getPropertyValue('--color-background').trim() || '255, 255, 255';
      const foreground = styles.getPropertyValue('--color-foreground').trim() || '18, 18, 18';
      const accent = styles.getPropertyValue('--color-accent').trim() || foreground;
      this.tooltip.style.setProperty('--upo-tooltip-background', `rgb(${background})`);
      this.tooltip.style.setProperty('--upo-tooltip-foreground', `rgb(${foreground})`);
      this.tooltip.style.setProperty('--upo-tooltip-foreground-rgb', foreground);
      this.tooltip.style.setProperty('--upo-tooltip-accent', `rgb(${accent})`);

      this.tooltipAnchor = anchor;
      this.tooltip.hidden = false;
      this.tooltip.classList.remove('universal-product-options__tooltip--visible');
      this.positionTooltip();
      void this.tooltip.offsetWidth;
      this.tooltip.classList.add('universal-product-options__tooltip--visible');
    }

    positionTooltip() {
      if (!this.tooltipAnchor || !this.tooltip || this.tooltip.hidden) return;
      const gutter = 12;
      const gap = 8;
      const width = Math.min(280, window.innerWidth - (gutter * 2));
      this.tooltip.style.width = `${width}px`;
      const anchor = this.tooltipAnchor.getBoundingClientRect();
      const tooltip = this.tooltip.getBoundingClientRect();
      let placement = 'top';
      let top = anchor.top - tooltip.height - gap;
      if (top < gutter) {
        placement = 'bottom';
        top = anchor.bottom + gap;
      }
      top = Math.max(gutter, Math.min(top, window.innerHeight - tooltip.height - gutter));
      const anchorCenter = anchor.left + (anchor.width / 2);
      const left = Math.max(gutter, Math.min(anchorCenter - (width / 2), window.innerWidth - width - gutter));
      const arrowLeft = Math.max(16, Math.min(anchorCenter - left, width - 16));
      this.tooltip.dataset.placement = placement;
      this.tooltip.style.left = `${left}px`;
      this.tooltip.style.top = `${top}px`;
      this.tooltip.style.setProperty('--upo-tooltip-arrow-left', `${arrowLeft}px`);
    }

    hideTooltip() {
      if (!this.tooltip) return;
      this.tooltip.hidden = true;
      this.tooltip.classList.remove('universal-product-options__tooltip--visible');
      this.tooltipAnchor = null;
    }


    renderSummary(states = this.flowStates()) {
      const summary = document.createElement('div');
      summary.className = 'universal-product-options__summary';
      const progress = document.createElement('span');
      progress.className = 'universal-product-options__progress';
      const completeCount = states.filter(({ state }) => state === 'complete').length;
      const current = states.find(({ state }) => state === 'current');
      progress.textContent = current
        ? `Step ${completeCount + 1} of ${states.length}: ${current.group.label}`
        : 'Configuration complete';
      const price = document.createElement('span');
      price.className = 'universal-product-options__price-row';
      const label = document.createElement('span');
      label.textContent = current ? 'Current estimated price' : 'Estimated configured price';
      const total = document.createElement('span');
      total.className = 'universal-product-options__total';
      total.textContent = this.money(this.basePriceCents() + this.priceDeltaCents());
      price.append(label, total);
      summary.append(progress, price);
      return summary;
    }

    basePriceCents() {
      return Number(this.dataset.basePriceCents || this.schema.base_price_cents || 0);
    }

    priceDeltaCents() {
      let total = 0;
      for (const group of this.activeGroups()) {
        const value = this.findValue(group, this.selection.get(group.id));
        total += Number(value?.price_delta_cents || 0);
      }
      return total;
    }

    money(value) {
      const currency = this.schema.currency || this.dataset.presentmentCurrency || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency
      }).format(Number(value || 0) / 100);
    }

    deltaLabel(value) {
      const amount = Number(value || 0);
      if (!amount) return '';
      return ` (${amount > 0 ? '+' : '−'}${this.money(Math.abs(amount))})`;
    }

    findProductForm() {
      const productFormId = this.dataset.productFormId;
      const exactForm = productFormId ? document.getElementById(productFormId) : null;
      if (exactForm) return exactForm;
      const sectionId = this.dataset.sectionId;
      const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : this.closest('.shopify-section');
      return section?.querySelector('product-form.product-form--buy-buttons form[action*="/cart/add"]')
        || section?.querySelector('form.product-form--buy-buttons')
        || section?.querySelector('form[action*="/cart/add"]')
        || document.querySelector('product-form.product-form--buy-buttons form[action*="/cart/add"]')
        || document.querySelector('form.product-form--buy-buttons')
        || document.querySelector('form[action*="/cart/add"]');
    }

    syncProductForm() {
      const nextForm = this.findProductForm();
      if (!nextForm) {
        this.fail('The product form was not found. Move the Product options block near the buy buttons.');
        return;
      }
      if (this.form !== nextForm) {
        this.form?.removeEventListener('submit', this.boundValidate, true);
        this.form?.removeEventListener('change', this.boundVariantChange);
        this.form = nextForm;
        this.boundValidate = (event) => this.validateBeforeSubmit(event);
        this.boundVariantChange = () => {
          if (this.syncVariantPrice()) this.render();
        };
        this.form.addEventListener('submit', this.boundValidate, true);
        this.form.addEventListener('change', this.boundVariantChange);
      }
      this.syncVariantPrice();
      this.form.querySelectorAll('[data-universal-product-option-property]').forEach((input) => input.remove());

      const selections = [];
      this.flowGroups().forEach((group, index) => {
        const value = this.findValue(group, this.selection.get(group.id));
        if (!this.selection.has(group.id)) return;
        selections.push({ group_id: group.id, value_id: value?.id || '' });
        this.addProperty(`Option ${String(index + 1).padStart(2, '0')}: ${group.label}`, value?.label || 'No option');
      });
      this.addProperty('_product_options_version', String(this.schema.version || 1));
      this.addProperty('_product_options_selection', JSON.stringify(selections));
      this.addProperty('_product_options_price_delta_cents', String(this.priceDeltaCents()));
      const incomplete = this.flowStates().some(({ state }) => state === 'current' || state === 'locked');
      this.form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
        button.classList.toggle('universal-product-options-submit--locked', incomplete);
        if (incomplete) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    }

    syncVariantPrice() {
      const variantInput = this.form?.querySelector('[name="id"]');
      const price = this.variantPrices?.get(String(variantInput?.value || ''));
      if (!Number.isFinite(price) || String(price) === this.dataset.basePriceCents) return false;
      this.dataset.basePriceCents = String(price);
      return true;
    }

    addProperty(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.dataset.universalProductOptionProperty = '';
      this.form.append(input);
    }

    validateBeforeSubmit(event) {
      const missing = this.flowStates().find(({ state }) => state === 'current')?.group;
      if (!missing) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.fail(`Complete ${missing.label} before adding this configuration to the cart.`);
      this.querySelector(`[data-option-group="${CSS.escape(missing.id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    fail(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }
  }

  customElements.define('universal-product-options', UniversalProductOptions);
})();
